import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Check, X, Tag } from "lucide-react";
import { toast } from "sonner";

const inputCls = "h-11 bg-[#EFE6D6] border-[#DCCFB8]";

// Fair-only model/color cards, independent from main stock cards
export default function FairProducts({ products, onChanged }) {
  const [form, setForm] = useState({ model_code: "", model_color: "" });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    try {
      if (editing) await api.put(`/fair/products/${editing}`, form);
      else await api.post("/fair/products", form);
      toast.success(editing ? "Fuar kartı güncellendi" : "Fuar kartı eklendi");
      setForm((f) => ({ model_code: editing ? "" : f.model_code, model_color: "" })); setEditing(null);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`${p.model_code} ${p.model_color} silinsin mi?`)) return;
    try { await api.delete(`/fair/products/${p.id}`); toast.success("Silindi"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  const models = [...new Set(products.map((p) => p.model_code))];

  return (
    <div className="space-y-5" data-testid="fair-products">
      <section className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5">
        <h2 className="font-display text-lg font-bold uppercase tracking-tight mb-3">{editing ? "Kartı Düzenle" : "Yeni Fuar Kartı"}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
          <Input placeholder="Model kodu *" value={form.model_code} data-testid="fair-product-code"
            onChange={(e) => setForm({ ...form, model_code: e.target.value })} className={inputCls} />
          <Input placeholder="Renk *" value={form.model_color} data-testid="fair-product-color"
            onChange={(e) => setForm({ ...form, model_color: e.target.value })} className={inputCls}
            onKeyDown={(e) => e.key === "Enter" && save()} />
          <div className="flex gap-2">
            <Button onClick={save} data-testid="fair-product-save" className="h-11 bg-[#D97706] hover:bg-[#B45309] text-white">
              {editing ? <><Check className="w-4 h-4 mr-1" /> Kaydet</> : <><Plus className="w-4 h-4 mr-1" /> Ekle</>}
            </Button>
            {editing && (
              <Button variant="outline" onClick={() => { setEditing(null); setForm({ model_code: "", model_color: "" }); }}
                data-testid="fair-product-cancel" className="h-11 border-[#DCCFB8] bg-transparent"><X className="w-4 h-4" /></Button>
            )}
          </div>
        </div>
        <p className="text-xs text-[#8C7B68] mt-2">Fuar kartları sadece fuar siparişlerinde kullanılır; stoka, kesime veya siparişlere işlenmez.</p>
      </section>

      {models.length === 0 && (
        <div className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-8 text-center text-[#8C7B68]" data-testid="fair-products-empty">Henüz fuar kartı yok.</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {models.map((m) => (
          <div key={m} className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-4" data-testid={`fair-model-${m}`}>
            <p className="font-display text-lg font-bold flex items-center gap-2 mb-2"><Tag className="w-4 h-4 text-[#B45309]" /> {m}</p>
            <div className="space-y-1">
              {products.filter((p) => p.model_code === m).map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded px-2 py-1.5 hover:bg-[#EFE6D6]">
                  <span className="text-sm">{p.model_color}</span>
                  <span className="flex gap-2">
                    <button onClick={() => { setEditing(p.id); setForm({ model_code: p.model_code, model_color: p.model_color }); }}
                      data-testid={`fair-product-edit-${p.id}`} className="text-[#8C7B68] hover:text-[#B45309]"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(p)} data-testid={`fair-product-delete-${p.id}`}
                      className="text-[#8C7B68] hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5" /></button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
